import type {Middleware} from '@reduxjs/toolkit';
import {TASK_SAVE, TASK_SAVE_FAILURE, TASK_SAVE_QUEUE} from './actions/taskActions';

/**
 * OWA-939 on the 6.7.4 release line. The Dashboard's Unsaved drafts panel saves a task and
 * must know whether the save was queued before it removes the task's unsaved versions.
 * On main the task saga reports that through saveTaskQueue(task, onQueued), added with
 * OWA-373 (a regeneration of every saga). This line's generated task saga has no report,
 * but it already ends each save one of two ways: TASK_SAVE once the task is written to
 * IndexedDB, or TASK_SAVE_FAILURE when it is not. Both carry the task object the save was
 * dispatched with. This middleware passes that outcome to the onQueued callback carried
 * on the TASK_SAVE_QUEUE action, once per save.
 */
type ReportableAction = {type?: unknown, task?: unknown, onQueued?: unknown};

const pending = new WeakMap<object, (saved: boolean) => void>();

export const taskSaveQueuedMiddleware: Middleware = () => next => action => {
    const {type, task, onQueued} = (action ?? {}) as ReportableAction;
    const key = task && typeof task === 'object' ? task : null;

    // Registered before the saga sees the action, so its outcome cannot arrive first.
    if (key && type === TASK_SAVE_QUEUE && typeof onQueued === 'function') {
        pending.set(key, onQueued as (saved: boolean) => void);
    }

    const result = next(action);

    if (key && (type === TASK_SAVE || type === TASK_SAVE_FAILURE)) {
        const report = pending.get(key);
        if (report) {
            pending.delete(key);
            try {
                report(type === TASK_SAVE);
            }
            catch (err) {
                console.error('Task save report failed', err);
            }
        }
    }

    return result;
};
